import { Helmet } from "react-helmet-async";
import { Phone, MessageCircle, ArrowLeft, MapPin, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";

const WHATSAPP_URL = "https://wa.me/919891315777?text=Hi%2C%20I%20want%20to%20book%20a%20water%20tank%20cleaning%20service%20in%20Gurugram";

const serviceLinks = [
  { href: "/residential-tank-cleaning-gurugram", label: "Residential Tank Cleaning" },
  { href: "/overhead-water-tank-cleaning-gurugram", label: "Overhead Tank Cleaning" },
  { href: "/underground-water-tank-cleaning-gurugram", label: "Underground Tank Cleaning" },
  { href: "/commercial-water-tank-cleaning-gurugram", label: "Commercial Tank Cleaning" },
];

const nearbyAreas = [
  { href: "/water-tank-cleaning-dlf-phase-gurugram", label: "DLF Phase 1-5" },
  { href: "/water-tank-cleaning-sushant-lok-gurugram", label: "Sushant Lok" },
  { href: "/water-tank-cleaning-palam-vihar-gurugram", label: "Palam Vihar" },
  { href: "/water-tank-cleaning-golf-course-road-gurugram", label: "Golf Course Road" },
];

export default function Blog2() {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>5 Signs Your Water Tank Needs Immediate Cleaning | TankBuddy Gurugram</title>
        <meta name="description" content="5 warning signs your water tank is contaminated and needs immediate cleaning. TankBuddy provides same-day service in Gurugram. Call +91 98913 15777" />
        <link rel="canonical" href="https://www.tankbuddy.in/blog/signs-water-tank-needs-cleaning" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <Header />

      <main className="pt-20 pb-16">
        <section className="max-w-4xl mx-auto px-4 md:px-8 py-12">

          <a href="/" className="inline-flex items-center gap-2 text-sky text-sm font-medium mb-8 hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </a>

          <div className="inline-flex items-center gap-2 bg-sky/10 text-sky text-sm font-medium px-3 py-1.5 rounded-full mb-6">
            TankBuddy Blog
          </div>

          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy leading-tight mb-6">
            5 Signs Your Water Tank Needs Immediate Cleaning
          </h1>

          <div className="prose prose-slate max-w-none mb-10">
            <p className="text-slate-600 leading-relaxed text-base">
              Most Gurugram families ignore their water tank until something goes seriously wrong. By the time you notice a problem, bacteria and algae may have been growing for months. Here are 5 warning signs you should never ignore.
            </p>
            <p className="text-slate-600 leading-relaxed text-base mt-4">
              If you notice any of these signs, book TankBuddy same-day cleaning service immediately. We serve all Gurugram sectors including DLF, Sushant Lok, Palam Vihar, Golf Course Road and all sectors.
            </p>
          </div>

          <div className="bg-navy rounded-xl p-8 mb-10 text-white">
            <h2 className="font-heading font-bold text-xl mb-2">Book Tank Cleaning in Gurugram</h2>
            <p className="text-slate-300 text-sm mb-5">Same-day service available. Starting Rs. 999 for residential tanks.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild className="bg-whatsapp hover:bg-whatsapp-hover text-white rounded-full px-6">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="w-4 h-4 mr-2" /> Book on WhatsApp
                </a>
              </Button>
              <Button asChild variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-6">
                <a href="tel:+919891315777">
                  <Phone className="w-4 h-4 mr-2" /> Call +91 98913 15777
                </a>
              </Button>
            </div>
          </div>

          <div className="mb-10">
            <h2 className="font-heading font-bold text-2xl text-navy mb-6">
              The 5 Warning Signs
            </h2>
            <div className="space-y-6">
              {[
                {num:"01", title:"Strange Smell or Taste in Water", desc:"If your tap water smells musty, earthy or has a metallic taste your tank is contaminated. Bacteria and algae produce gases that cause these odours. This is the most common sign in Gurugram homes."},
                {num:"02", title:"Discoloured Water", desc:"Yellow, brown or greenish water is a serious warning sign. It indicates rust, sediment or algae growth inside your tank. Never drink discoloured water - call TankBuddy immediately."},
                {num:"03", title:"Frequent Stomach Problems in Family", desc:"If family members are frequently experiencing diarrhoea, nausea or stomach cramps - contaminated water could be the cause. Waterborne bacteria like E.coli and Salmonella thrive in dirty tanks."},
                {num:"04", title:"Visible Slime or Algae", desc:"If you can see greenish or black slime on the inside of your tank walls or lid it needs immediate cleaning. Algae grows rapidly in Gurugram summer heat."},
                {num:"05", title:"It Has Been More Than 6 Months", desc:"Even if your water looks and smells fine - bacteria invisible to the naked eye could still be present. WHO recommends cleaning every 6 months minimum."},
              ].map((item) => (
                <div key={item.num} className="bg-surface border border-slate-200 rounded-lg p-5">
                  <span className="text-sky font-bold text-sm">Sign {item.num}</span>
                  <h3 className="font-heading font-bold text-navy mt-1 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-10">
            <h2 className="font-heading font-bold text-2xl text-navy mb-4">
              Who Is Most at Risk?
            </h2>
            <ul className="space-y-3">
              {[
                "Children under 5 years",
                "Elderly people above 60",
                "Pregnant women",
                "People with weak immunity"
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-600">
                  <div className="w-5 h-5 rounded-full bg-sky/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-sky" />
                  </div>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="mb-10">
            <h2 className="font-heading font-bold text-xl text-navy mb-4">Our Services in Gurugram</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {serviceLinks.map(link => (
                <a key={link.href} href={link.href} className="flex items-center gap-3 bg-surface border border-slate-200 rounded-lg p-4 hover:border-sky/50 hover:shadow-sm transition-all">
                  <div className="w-8 h-8 rounded-lg bg-sky/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-sky" />
                  </div>
                  <span className="text-sm font-medium text-navy">{link.label}</span>
                </a>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-heading font-bold text-xl text-navy mb-4">Also Serving Nearby Areas</h2>
            <div className="flex flex-wrap gap-2">
              {nearbyAreas.map(a => (
                <a key={a.href} href={a.href} className="inline-flex items-center gap-1 bg-surface border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full hover:border-sky/50 hover:text-navy transition-colors">
                  <MapPin className="w-3 h-3 text-sky" />{a.label}
                </a>
              ))}
            </div>
          </div>

        </section>
      </main>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
